<template>
  <div class="enhanced-sms-input-bar">
    <!-- Main input area -->
    <div class="input-container">
      <!-- Send Options Dropdown -->
      <div class="send-options-dropdown" :class="{ 'open': sendOptionsOpen }">
        <button 
          class="options-trigger"
          @click="toggleSendOptions"
          :class="{ 'active': sendOptionsOpen }"
          :disabled="disabled"
          type="button"
        >
          <i class="fa fa-plus"></i>
        </button>
        
        <!-- Dropdown menu -->
        <div class="options-menu" v-if="sendOptionsOpen">
          <div class="option-item" @click="selectSendOption('wiki')">
            <i class="fa fa-book"></i>
            <span>Send Wiki Article</span>
          </div>
          <div class="option-item" @click="selectSendOption('form')">
            <i class="fa fa-clipboard"></i>
            <span>Send Form Link</span>
          </div>
          <!-- Upload File option hidden - MMS messages are expensive and users can send images through two-way chat -->
          <!-- <div class="option-item" @click="selectSendOption('file')">
            <i class="fa fa-upload"></i>
            <span>Upload File</span>
          </div> -->
          <div class="option-item" @click="selectSendOption('template')">
            <i class="fa fa-file-text-o"></i>
            <span>SMS Template</span>
          </div>
        </div>
      </div>
      
      <!-- Message Input (hidden when template is loaded) -->
      <input
        v-if="!loadedTemplate"
        v-model="messageText"
        type="text"
        placeholder="Type your SMS message..."
        @keyup.enter="sendMessage"
        @keydown.esc="clearMessage"
        class="message-input"
        :disabled="disabled"
        ref="messageInput"
      />

      <!-- Expanded textarea when template is loaded -->
      <textarea
        v-if="loadedTemplate"
        v-model="messageText"
        class="message-input template-textarea"
        :disabled="disabled"
        ref="templateTextarea"
        @input="autoResizeTextarea"
        @keydown.esc="clearTemplate"
        rows="3"
      ></textarea>

      <!-- Send Button -->
      <button
        class="send-button"
        @click="sendMessage"
        :disabled="!canSend || disabled"
        type="button"
      >
        <i class="fa fa-paper-plane"></i>
      </button>
    </div>

    <!-- Template badge + character count -->
    <div class="sms-meta" v-if="loadedTemplate || messageText.length > 0">
      <div class="template-badge" v-if="loadedTemplate">
        <i class="fa fa-file-text-o"></i>
        <span>{{ loadedTemplate.Name }}</span>
        <button class="clear-template-btn" @click="clearTemplate" title="Clear template">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Shared SMS guidance: live credit counter + warn/block banner + tips (utils/smsGuidance) -->
    <SmsGuidance :text="messageText" @change="onSmsGuidance" />
    
    <!-- Option-specific modals -->
    <WikiSelectionModal 
      v-if="selectedSendOption === 'wiki'"
      @send="handleSendWiki"
      @cancel="closeSendOption"
    />
    
    <FormSelectionModal 
      v-if="selectedSendOption === 'form'"
      @send="handleSendForm"
      @cancel="closeSendOption"
    />
    
    <FileUploadModal 
      v-if="selectedSendOption === 'file'"
      @send="handleSendFile"
      @cancel="closeSendOption"
    />
    
    <SmsTemplateSelectionModal
      v-if="selectedSendOption === 'template'"
      @send="handleSendTemplate"
      @cancel="closeSendOption"
    />

  </div>
</template>

<script>
import WikiSelectionModal from '../chat/modals/WikiSelectionModal.vue'
import FormSelectionModal from '../chat/modals/FormSelectionModal.vue'
import FileUploadModal from '../chat/modals/FileUploadModal.vue'
import SmsTemplateSelectionModal from './modals/SmsTemplateSelectionModal.vue'
import SmsGuidance from '../../../../components/SmsGuidance.vue'

export default {
  name: 'EnhancedSmsInputBar',
  components: {
    WikiSelectionModal,
    FormSelectionModal,
    FileUploadModal,
    SmsTemplateSelectionModal,
    SmsGuidance
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      messageText: '',
      sendOptionsOpen: false,
      selectedSendOption: null,
      loadedTemplate: null,  // { WikiPageID, Name, Content, linkMap } when a template is loaded for editing
      smsBlocked: false,     // set by <SmsGuidance> when the text is over the carrier limit
      lastGuidance: null     // latest <SmsGuidance> payload, for the optimistic credit draw
    }
  },
  computed: {
    canSend() {
      return this.messageText.trim().length > 0 && !this.smsBlocked
    }
  },
  methods: {
    onSmsGuidance(g) {
      // Disable Send when the message can't be delivered (over the carrier segment cap).
      this.smsBlocked = !!(g && g.blocked)
      this.lastGuidance = g || null
    },

    // Optimistic credit draw when THIS user fires a send — instant feedback on the
    // header pill; the next siteWallet snapshot reconciles the authoritative number.
    applyOptimisticCreditDraw() {
      const credits = this.lastGuidance && this.lastGuidance.billableCredits
        ? this.lastGuidance.billableCredits : 0
      if (credits > 0) {
        try { this.$store.commit('wallet/applyOptimisticDraw', credits) } catch (e) { /* wallet not registered */ }
      }
    },
    toggleSendOptions() {
      if (this.disabled) return
      this.sendOptionsOpen = !this.sendOptionsOpen
    },
    
    selectSendOption(option) {
      this.selectedSendOption = option
      this.sendOptionsOpen = false
    },
    
    closeSendOption() {
      this.selectedSendOption = null
    },
    
    restoreLinksFromPlaceholders(text) {
      // Replace [Form: ...] and [Link: ...] placeholders back to HTML anchors
      if (!this.loadedTemplate || !this.loadedTemplate.linkMap) return text
      let restored = text
      for (const [placeholder, html] of Object.entries(this.loadedTemplate.linkMap)) {
        restored = restored.replace(placeholder, html)
      }
      return restored
    },

    async sendMessage() {
      if (!this.canSend || this.disabled) return

      this.applyOptimisticCreditDraw()

      if (this.loadedTemplate) {
        // Restore [Form:]/[Link:] placeholders back to HTML anchors before sending
        const editedText = this.restoreLinksFromPlaceholders(this.messageText.trim())
        // Sending a wiki template (possibly edited) — emit send-wiki with restored content
        this.$emit('send-wiki', {
          WikiPageID: this.loadedTemplate.WikiPageID,
          Name: this.loadedTemplate.Name,
          Content: this.loadedTemplate.Content,  // original content for reference
          EditedContent: editedText  // user's edited text with links restored to HTML
        })
        this.clearTemplate()
      } else {
        this.$emit('send-message', {
          content: this.messageText.trim()
        })
        this.clearMessage()
      }
    },

    clearMessage() {
      this.messageText = ''
      this.$refs.messageInput?.focus()
    },

    clearTemplate() {
      this.loadedTemplate = null
      this.messageText = ''
      this.$nextTick(() => {
        this.$refs.messageInput?.focus()
      })
    },

    loadTemplateIntoEditor(wikiData) {
      // Load wiki template content into the editable textarea
      this.loadedTemplate = {
        WikiPageID: wikiData.WikiPageID,
        Name: wikiData.Name,
        Content: wikiData.Content,
        linkMap: wikiData.linkMap || {}  // Map of [Form:]/[Link:] placeholders → original HTML anchors
      }
      this.messageText = wikiData.Content || ''
      this.$nextTick(() => {
        this.autoResizeTextarea()
        this.$refs.templateTextarea?.focus()
      })
    },

    autoResizeTextarea() {
      const textarea = this.$refs.templateTextarea
      if (textarea) {
        textarea.style.height = 'auto'
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'
      }
    },

    handleSendWiki(wikiData) {
      // Load into editable textarea instead of sending immediately
      this.loadTemplateIntoEditor(wikiData)
      this.closeSendOption()
    },

    handleSendForm(formData) {
      this.$emit('send-form', formData)
      this.closeSendOption()
    },

    handleSendFile(fileData) {
      this.$emit('send-file', fileData)
      this.closeSendOption()
    },

    handleSendTemplate(templateData) {
      if (templateData.wikiPageID) {
        // Wiki-based template — load into editor for review/editing
        this.loadTemplateIntoEditor({
          WikiPageID: templateData.wikiPageID,
          Name: templateData.title,
          Content: templateData.content,
          linkMap: templateData.linkMap || {}
        })
      } else {
        // Hardcoded template (no variable processing) — populate input directly
        this.messageText = templateData.content
        this.$refs.messageInput?.focus()
      }
      this.closeSendOption()
    },
    
    handleClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.sendOptionsOpen = false
      }
    }
  },
  
  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },
  
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>

<style lang="scss" scoped>
.enhanced-sms-input-bar {
  position: relative;
  padding: 16px;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.input-container {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border-radius: 24px;
  padding: 4px;
}

.send-options-dropdown {
  position: relative;
  
  .options-trigger {
    width: 40px;
    height: 40px;
    border: none;
    background: transparent;
    color: #666;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    position: relative;
    
    &:hover:not(:disabled) {
      background: rgba(25, 118, 210, 0.1);
      color: #1976d2;
    }
    
    &.active {
      background: #1976d2;
      color: white;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  .options-menu {
    position: absolute;
    bottom: 50px;
    left: 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.15);
    z-index: 1000;
    min-width: 200px;
    overflow: hidden;
    
    .option-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      cursor: pointer;
      transition: background 0.2s ease;
      
      &:hover {
        background: #f5f5f5;
      }
      
      .fa {
        width: 20px;
        margin-right: 12px;
        color: #666;
      }
      
      span {
        font-size: 14px;
        color: #333;
      }
    }
  }
}

.message-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 16px;
  font-size: 14px;
  outline: none;
  color: #333;

  &::placeholder {
    color: #999;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.template-textarea {
    resize: none;
    line-height: 1.4;
    min-height: 60px;
    max-height: 200px;
    font-family: inherit;
  }
}

.send-button {
  width: 40px;
  height: 40px;
  border: none;
  background: #1976d2;
  color: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #1565c0;
    transform: scale(1.05);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
  
  .fa {
    font-size: 14px;
  }
}

.sms-meta {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.template-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #e3f2fd;
  color: #1565c0;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;

  span {
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .clear-template-btn {
    border: none;
    background: transparent;
    color: #1565c0;
    cursor: pointer;
    padding: 0 2px;
    font-size: 11px;
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
}

.sms-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  margin-left: auto;

  .char-count {
    color: #666;

    &.warning {
      color: #ff9800;
    }

    &.error {
      color: #f44336;
    }
  }

  .sms-count {
    color: #999;
    font-style: italic;
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .enhanced-sms-input-bar {
    padding: 12px;
  }
  
  .send-options-dropdown .options-menu {
    left: -50px;
    min-width: 180px;
  }
}
</style>
